import { CalendarMonth } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { debounce } from "lodash";
import React, { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Timesheet from "../components/Timesheet";
import { useApiMutation } from "../hooks/useApi";
import { TimeSheet } from "../types/timesheet";
import UnapproveTimesheetDialog from "./UnapproveTimesheetDialog";

interface Props {
  timesheet: TimeSheet;
  onApprove: (timesheet: TimeSheet) => void;
  onAmendClick: (timesheet: TimeSheet) => void;
}

export const TimesheetExpandedContent: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const { timesheet, onApprove, onAmendClick } = props;
  const [btnOpen, setBtnOpen] = useState<boolean>(false);
  const [unapproveOpen, setUnapproveOpen] = useState<boolean>(false);
  const [isDisabled] = useState<boolean>(!timesheet.canAmend);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const { apiUpdate } = useApiMutation();

  const { t } = useTranslation();

  const location = useLocation();

  const debouncedApiUpdate = useCallback(
    debounce((status) => {
      apiUpdate.mutate(
        {
          entity: "timesheets",
          post: {
            id: timesheet?.id,
            status: status,
          },
        },
        {
          onError: (error) => console.error(error),
        }
      );
    }, 5000),
    [timesheet, apiUpdate]
  );

  const handleApproveClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    onApprove(timesheet);
    const status = 1;
    debouncedApiUpdate(status);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => {
    // setAmendOpen(true);
    setBtnOpen(false);
    onAmendClick(timesheet);
  };

  const handleBtnClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setBtnOpen(false);
  };

  const handleToggle = () => {
    setBtnOpen((prevOpen) => !prevOpen);
  };

  const handleUnapproveClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setUnapproveOpen(true);
  };

  const handleUnapproveClose = () => {
    setUnapproveOpen(false);
  };

  return (
    <>
      <Stack
        direction={"column"}
        padding={1}
        spacing={2}
        sx={{ justifyContent: "space-between", mt: "20px", mb: "20px" }}
      >
        {location.pathname === "/history" && (
          <>
            <Stack
              direction={"row"}
              padding={0}
              spacing={2}
              justifyContent={"left"}
              width={"100%"}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <PersonIcon
                  fontSize="large"
                  sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2">
                  {" "}
                  {timesheet.approvedBy}{" "}
                </Typography>
                <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                  {" "}
                  {t("tsTable.approver")}{" "}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction={"row"}
              padding={0}
              spacing={2}
              justifyContent={"left"}
              width={"100%"}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <CalendarMonth
                  fontSize="large"
                  sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2">
                  {format(timesheet.approvedDate!, "dd/MM/yyyy")}
                </Typography>
                <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                  {" "}
                  {t("tsTable.dateApproved")}{" "}
                </Typography>
              </Box>
            </Stack>
          </>
        )}

        <Stack
          direction={"row"}
          padding={0}
          spacing={2}
          justifyContent={"left"}
          width={"100%"}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <PersonIcon fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2">
              {" "}
              {timesheet.bookingContact}{" "}
            </Typography>
            <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
              {" "}
              {t("tsTable.bookingContact")}{" "}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={"row"} padding={0} spacing={2} width={"100%"}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <PersonIcon fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2">
              {" "}
              {timesheet.owningConsultant}{" "}
            </Typography>
            <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
              {" "}
              {t("tsTable.owningConsultant")}{" "}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={"row"} padding={0} spacing={2} width={"100%"}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <BusinessIcon
              fontSize="large"
              sx={{ color: "rgba(0, 0, 0, 0.5)" }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2"> {timesheet.location} </Typography>
            <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
              {" "}
              {t("tsTable.location")}{" "}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={"row"} padding={0} spacing={2} width={"100%"}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <SchoolIcon fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2"> {timesheet.jobTitle} </Typography>
            <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
              {" "}
              {t("tsTable.jobDescription")}{" "}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={"row"} padding={0} spacing={2} width={"100%"}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <PersonIcon fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2"> {timesheet.clientName} </Typography>
            <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
              {" "}
              {t("tsTable.clientName")}{" "}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Timesheet days={timesheet.days} variant="small" timesheet={timesheet} />

      {timesheet.status === 0 || timesheet.status === 2 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: "30px",
            pb: "10px",
          }}
        >
          <ButtonGroup
            variant="contained"
            ref={anchorRef}
            aria-label="Button group with a nested menu"
            size="small"
            sx={{ height: "30.08px" }}
            disabled={isDisabled}
          >
            <Button
              onClick={handleApproveClick}
              startIcon={
                <CheckCircleIcon
                  fontSize="small"
                  sx={{ color: isDisabled ? "#A6A6A6" : "white" }}
                />
              }
            >
              {t("buttons.approve")}
            </Button>
            <Button
              size="small"
              aria-controls={"split-button-menu"}
              aria-expanded={"true"}
              aria-label="select merge strategy"
              aria-haspopup="menu"
              onClick={handleToggle}
            >
              <ArrowDropDownIcon
                sx={{ color: isDisabled ? "#A6A6A6" : "white" }}
              />
            </Button>
          </ButtonGroup>
          <Popper
            sx={{
              zIndex: 1,
            }}
            open={btnOpen}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleBtnClose}>
                    <MenuList id="split-button-menu" autoFocusItem>
                      <MenuItem onClick={(event) => handleMenuItemClick(event)}>
                        <EditIcon />
                        {t("tsTable.amend")}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      ) : timesheet.status === 1 ? (
        <Stack
          direction={"row"}
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: "30px",
            pb: "10px",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={
              <HighlightOffIcon
                sx={{ color: isDisabled ? "#BDBDBD" : "#005292" }}
              />
            }
            onClick={(event) => handleUnapproveClick(event)}
            disabled={isDisabled}
          >
            {t("buttons.unapprove")}
          </Button>
        </Stack>
      ) : (
        timesheet.status === 3 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: "30px",
              pb: "10px",
            }}
          >
            <Typography variant="body1">
              {" "}
              {t("tsTable.reviewPending")}{" "}
            </Typography>
          </Box>
        )
      )}

      <UnapproveTimesheetDialog
        unapproveOpen={unapproveOpen}
        handleUnapproveClose={handleUnapproveClose}
        timesheet={timesheet}
      />
    </>
  );
};

export default TimesheetExpandedContent;
